<template>
  <a-modal
    :open="open"
    :footer="null"
    :closable="false"
    :width="448"
    :destroyOnClose="true"
    centered
    wrapClassName="feedback-form-modal"
    @cancel="close"
  >
    <div class="bg-white rounded-2xl overflow-hidden">
      <!-- Header -->
      <div class="px-6 pt-6 pb-4 border-b border-gray-100">
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-start gap-3">
            <div
              class="size-10 rounded-lg bg-[#E0EDFF] flex items-center justify-center shrink-0"
            >
              <FlagIcon class="size-5 text-[#4A8EEE]" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-[#1E3456] m-0">
                {{ title }}
              </h3>
              <p class="text-sm text-gray-500 mt-0.5 mb-0">
                {{ subtitle }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="size-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all cursor-pointer shrink-0"
            aria-label="Close"
            @click="close"
          >
            <XIcon class="size-5" />
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
        <div>
          <label class="text-sm font-medium text-gray-700 mb-2 block">
            What's wrong?
          </label>
          <div class="space-y-1.5" role="radiogroup">
            <button
              v-for="option in reasons"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="reason === option.value"
              class="w-full px-3.5 py-2.5 rounded-lg flex items-center justify-between text-left cursor-pointer transition-all duration-150 border"
              :class="
                reason === option.value
                  ? 'bg-[#E0EDFF]/60 border-[#4A8EEE]/30 text-[#1E3456]'
                  : 'bg-gray-50 border-transparent hover:bg-gray-100 text-gray-700'
              "
              @click="emit('update:reason', option.value)"
            >
              <span class="text-sm font-medium">{{ option.label }}</span>
              <CheckIcon
                v-if="reason === option.value"
                class="size-4 text-[#4A8EEE]"
              />
            </button>
          </div>
        </div>
        <div v-if="detailsVisible">
          <textarea
            :value="comment"
            @input="emit('update:comment', $event.target.value)"
            rows="3"
            :maxlength="COMMENT_MAX_LENGTH"
            placeholder="Describe the issue in detail for us to fix"
            class="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 resize-y outline-none focus:border-[#4A8EEE]/50 focus:ring-2 focus:ring-[#4A8EEE]/10 transition-all"
          />
          <p class="mt-1 mb-0 text-right text-xs text-gray-400">
            {{ comment.length }} / {{ COMMENT_MAX_LENGTH }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2"
      >
        <button
          v-if="removeLabel"
          type="button"
          class="px-2 py-2 rounded-lg text-sm font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="busy"
          @click="emit('remove')"
        >
          <Loader2 v-if="removing" class="inline size-4 animate-spin mr-1" />
          {{ removeLabel }}
        </button>
        <span v-else />
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-all cursor-pointer"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#4A8EEE] hover:bg-[#3B7FDD] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            :disabled="!submittable || busy"
            @click="emit('submit')"
          >
            <Loader2 v-if="submitting" class="size-4 animate-spin" />
            <SendIcon v-else class="size-4" />
            Submit
          </button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
// Presentational shell shared by the question flag / "Report an issue" modal
// and "Report sorty's behaviour": reason cards, an optional details box and
// the footer. The caller owns the form state and what submit/remove do.
import { CheckIcon, FlagIcon, Loader2, SendIcon, XIcon } from 'lucide-vue-next';
import { COMMENT_MAX_LENGTH } from '@/utils/questionFlags';

defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  // [{ value, label }]
  reasons: { type: Array, required: true },
  reason: { type: String, default: null },
  comment: { type: String, default: '' },
  detailsVisible: { type: Boolean, default: false },
  submittable: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
  // Shown (with a `remove` event) only when there is something to remove.
  removeLabel: { type: String, default: '' },
  removing: { type: Boolean, default: false },
});
const emit = defineEmits([
  'update:open',
  'update:reason',
  'update:comment',
  'submit',
  'remove',
]);

function close() {
  emit('update:open', false);
}
</script>

<style>
.feedback-form-modal .ant-modal-content {
  padding: 0;
  border-radius: 1rem;
  overflow: hidden;
}
</style>
